/**
 * Admin — Clock Event Integrity Diagnostic Viewer
 * ─────────────────────────────────────────────
 * Phase 5 (2026-02) — Renders the authoritative read-only diagnostic
 * from `GET /api/broadcast/clock_event_integrity/{station_id}` in a
 * navigable UI so operators can correlate:
 *
 *   Clock UI → stream_songs → gaps → authoritative BREAK decision
 *          → classifier evidence
 *
 * Auth: existing SPA Bearer JWT from localStorage. The backend endpoint
 * is admin-gated (`role == 'admin'`); non-admins receive HTTP 403.
 *
 * Never mutates any Mongo document. Never changes BREAK thresholds,
 * classifier weights or scoring. Pure projection.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Loader2, AlertCircle, Download, Copy,
  Music2, ListMusic, Radio, Activity, ChevronRight, ChevronDown,
} from 'lucide-react';
import { getBackendUrl } from '../../utils/api';

const API = getBackendUrl();

const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const fmt = (v) => {
  if (v == null) return '—';
  if (typeof v === 'number') {
    if (Number.isInteger(v)) return v.toLocaleString('el-GR');
    return v.toFixed(2);
  }
  return String(v);
};

const SectionHeader = ({ title, count, icon: Icon, open, onToggle, testId }) => (
  <button
    onClick={onToggle}
    className="w-full flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-left"
    data-testid={testId}
  >
    {open ? (
      <ChevronDown className="w-4 h-4 text-white/60" />
    ) : (
      <ChevronRight className="w-4 h-4 text-white/60" />
    )}
    {Icon && <Icon className="w-4 h-4 text-amber-300" />}
    <span className="text-[13px] font-semibold text-white/90">{title}</span>
    {count != null && (
      <span className="ml-auto text-[11px] text-white/50 tabular-nums">
        {count.toLocaleString('el-GR')}
      </span>
    )}
  </button>
);

const KeyValueGrid = ({ obj, testId }) => (
  <div
    className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px]"
    data-testid={testId}
  >
    {Object.entries(obj || {}).map(([k, v]) => (
      <div key={k} className="flex items-baseline gap-2">
        <span className="text-white/45 uppercase tracking-wider text-[10px] shrink-0">
          {k}
        </span>
        <span className="text-white/90 tabular-nums break-all">
          {v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
            ? fmt(v)
            : JSON.stringify(v)}
        </span>
      </div>
    ))}
  </div>
);

const JsonBlock = ({ data, testId }) => (
  <pre
    className="text-[11px] leading-relaxed bg-black/40 border border-white/10 rounded-lg p-3 overflow-x-auto max-h-[420px] overflow-y-auto text-white/80 font-mono"
    data-testid={testId}
  >
    {JSON.stringify(data, null, 2)}
  </pre>
);

const ClockEventIntegrity = () => {
  const navigate = useNavigate();
  const { stationId: routeStationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState(routeStationId || '');
  const [date, setDate] = useState(searchParams.get('date') || yesterdayISO());
  const [minGapSeconds, setMinGapSeconds] = useState(
    Number(searchParams.get('min_gap_seconds')) || 30,
  );

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Section-open toggles
  const [open, setOpen] = useState({
    summary: true,
    songs: false,
    gaps: false,
    breaks: true,
    raw: false,
  });
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  // Load station list (uses stream-monitor listing which we already
  // use elsewhere in the admin UI).
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API}/api/stream-monitor/stations`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const list = j?.stations || [];
        setStations(list);
        if (!routeStationId && list.length && !stationId) {
          setStationId(list[0]._id || list[0].id);
        }
      })
      .catch(() => setStations([]));
  }, [routeStationId, stationId]);

  const load = useCallback(async () => {
    if (!stationId) return;
    setBusy(true);
    setError(null);
    setData(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${API}/api/broadcast/clock_event_integrity/${stationId}?date=${date}&min_gap_seconds=${minGapSeconds}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        let detail = `HTTP ${r.status}`;
        try {
          const j = await r.json();
          if (j?.detail) detail = j.detail;
        } catch (_) { /* body may be empty */ }
        if (r.status === 401) detail = 'Not authenticated — please log in as admin.';
        if (r.status === 403) detail = 'Admin access required.';
        throw new Error(detail);
      }
      setData(await r.json());
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }, [stationId, date, minGapSeconds]);

  // Keep URL in sync so the admin can bookmark / share the exact view.
  useEffect(() => {
    const params = {};
    if (date) params.date = date;
    if (minGapSeconds && minGapSeconds !== 30) params.min_gap_seconds = String(minGapSeconds);
    setSearchParams(params, { replace: true });
    if (stationId && stationId !== routeStationId) {
      navigate(`/admin/clock-integrity/${stationId}?${new URLSearchParams(params).toString()}`, { replace: true });
    }
  }, [stationId, date, minGapSeconds, routeStationId, navigate, setSearchParams]);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    } catch (_) {
      /* ignore */
    }
  };

  const downloadJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clock_integrity_${stationId}_${date}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const summary = data?.summary || {};
  const songs = data?.songs || [];
  const gaps = data?.gaps || [];
  const breaks = data?.breaks || [];

  const stationName = useMemo(() => {
    const s = stations.find(
      (x) => (x._id || x.id) === stationId,
    );
    return s?.name || stationId || '—';
  }, [stations, stationId]);

  return (
    <div
      className="min-h-screen bg-gray-950 text-white px-4 sm:px-8 py-6"
      data-testid="admin-clock-integrity"
    >
      {/* Header */}
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-[12px] text-white/60 hover:text-white/95"
            data-testid="clock-integrity-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <h1 className="text-2xl font-bold tracking-tight">
            Clock Event Integrity
          </h1>
          <span className="text-[11px] text-white/40 uppercase tracking-wider">
            Phase 5 diagnostic
          </span>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 bg-white/5 border border-white/10 rounded-lg p-3">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-white/45 uppercase tracking-wider">
              Station
            </span>
            <select
              value={stationId}
              onChange={(e) => setStationId(e.target.value)}
              className="bg-slate-900 border border-white/10 rounded px-2 py-1.5 text-[12px] min-w-[220px]"
              data-testid="clock-integrity-station-select"
            >
              <option value="">— pick station —</option>
              {stations.map((s) => (
                <option key={s._id || s.id} value={s._id || s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-white/45 uppercase tracking-wider">
              Date
            </span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="bg-slate-900 border border-white/10 rounded px-2 py-1.5 text-[12px]"
              data-testid="clock-integrity-date-input"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-white/45 uppercase tracking-wider">
              min_gap_seconds
            </span>
            <select
              value={minGapSeconds}
              onChange={(e) => setMinGapSeconds(Number(e.target.value))}
              className="bg-slate-900 border border-white/10 rounded px-2 py-1.5 text-[12px]"
              data-testid="clock-integrity-mingap-select"
            >
              <option value={15}>15s</option>
              <option value={30}>30s</option>
              <option value={60}>60s</option>
              <option value={120}>120s</option>
              <option value={300}>300s</option>
            </select>
          </label>
          <button
            onClick={load}
            disabled={!stationId || busy}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[12px] font-medium ${
              !stationId || busy
                ? 'bg-white/5 text-white/30 cursor-not-allowed'
                : 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30'
            }`}
            data-testid="clock-integrity-load-btn"
          >
            {busy ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</>
            ) : (
              <><RefreshCw className="w-3.5 h-3.5" /> Load</>
            )}
          </button>
          <div className="ml-auto flex items-center gap-2">
            {data && (
              <>
                <button
                  onClick={copyJson}
                  className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded text-[11px] bg-white/5 hover:bg-white/10 border border-white/10"
                  data-testid="clock-integrity-copy-json-btn"
                >
                  <Copy className="w-3 h-3" /> Copy JSON
                </button>
                <button
                  onClick={downloadJson}
                  className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded text-[11px] bg-white/5 hover:bg-white/10 border border-white/10"
                  data-testid="clock-integrity-download-json-btn"
                >
                  <Download className="w-3 h-3" /> Download
                </button>
              </>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div
            className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-[12px] flex items-center gap-2"
            data-testid="clock-integrity-error"
          >
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Body */}
        {data && (
          <div className="space-y-3" data-testid="clock-integrity-body">
            {/* Title + counters */}
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex flex-wrap items-center gap-4">
              <div>
                <div className="text-[11px] text-white/50 uppercase tracking-wider">Station</div>
                <div className="text-sm font-semibold text-white/95" data-testid="clock-integrity-station-name">{stationName}</div>
              </div>
              <div>
                <div className="text-[11px] text-white/50 uppercase tracking-wider">Date</div>
                <div className="text-sm font-mono">{data.date}</div>
              </div>
              <div>
                <div className="text-[11px] text-white/50 uppercase tracking-wider">Phase</div>
                <div className="text-sm font-mono text-amber-300">{data.phase}</div>
              </div>
              <div className="ml-auto flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-[12px]">
                  <Music2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-white/60">Songs</span>
                  <span className="font-semibold text-white tabular-nums" data-testid="clock-integrity-songs-count">
                    {songs.length}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[12px]">
                  <ListMusic className="w-3.5 h-3.5 text-white/50" />
                  <span className="text-white/60">Gaps</span>
                  <span className="font-semibold text-white tabular-nums" data-testid="clock-integrity-gaps-count">
                    {gaps.length}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[12px]">
                  <Radio className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-white/60">Breaks</span>
                  <span className="font-semibold text-white tabular-nums" data-testid="clock-integrity-breaks-count">
                    {breaks.length}
                  </span>
                </div>
              </div>
            </div>

            {/* SUMMARY */}
            <SectionHeader
              title="Summary (SOURCE_INTEGRITY / DURATION / DUPLICATES / GAPS / BREAKS / CLASSIFIER)"
              icon={Activity}
              open={open.summary}
              onToggle={() => toggle('summary')}
              testId="clock-integrity-summary-header"
            />
            {open.summary && (
              <div className="space-y-2 pl-2" data-testid="clock-integrity-summary-body">
                {Object.entries(summary).map(([bucket, obj]) => (
                  <div
                    key={bucket}
                    className="bg-white/5 border border-white/10 rounded-lg p-3"
                    data-testid={`clock-integrity-summary-${bucket.toLowerCase()}`}
                  >
                    <div className="text-[11px] font-bold tracking-wider text-amber-300 uppercase mb-2">
                      {bucket}
                    </div>
                    <KeyValueGrid obj={obj} testId={`clock-integrity-summary-${bucket.toLowerCase()}-grid`} />
                  </div>
                ))}
              </div>
            )}

            {/* BREAKS */}
            <SectionHeader
              title="Authoritative BREAK decisions"
              icon={Radio}
              count={breaks.length}
              open={open.breaks}
              onToggle={() => toggle('breaks')}
              testId="clock-integrity-breaks-header"
            />
            {open.breaks && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-2" data-testid="clock-integrity-breaks-body">
                <JsonBlock data={breaks} testId="clock-integrity-breaks-json" />
              </div>
            )}

            {/* GAPS */}
            <SectionHeader
              title="Gaps (with classifier evidence)"
              icon={ListMusic}
              count={gaps.length}
              open={open.gaps}
              onToggle={() => toggle('gaps')}
              testId="clock-integrity-gaps-header"
            />
            {open.gaps && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-2" data-testid="clock-integrity-gaps-body">
                <JsonBlock data={gaps} testId="clock-integrity-gaps-json" />
              </div>
            )}

            {/* SONGS */}
            <SectionHeader
              title="Songs (SOT stream_songs projection)"
              icon={Music2}
              count={songs.length}
              open={open.songs}
              onToggle={() => toggle('songs')}
              testId="clock-integrity-songs-header"
            />
            {open.songs && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-2" data-testid="clock-integrity-songs-body">
                <JsonBlock data={songs} testId="clock-integrity-songs-json" />
              </div>
            )}

            {/* Raw */}
            <SectionHeader
              title="Full raw response"
              open={open.raw}
              onToggle={() => toggle('raw')}
              testId="clock-integrity-raw-header"
            />
            {open.raw && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-2" data-testid="clock-integrity-raw-body">
                <JsonBlock data={data} testId="clock-integrity-raw-json" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClockEventIntegrity;
